import {useMemo, useCallback, useState} from 'react';
import {BookingSummary, SessionStorage} from '@chekin/core';
import {SHOWED_UPSELLING_DEALS_MODAL} from 'utils/constants';
import {useFetchCheckIfOffersExists} from 'hooks/useFetchCheckIfOffersExists';

type UseShowDealsOnRegistrationProps = {
  housingId?: string;
  summary?: BookingSummary;
  enabled?: boolean;
  reservationId?: string;
};

export const useShowDealsOnRegistration = ({
  housingId,
  summary,
  enabled = true,
  reservationId,
}: UseShowDealsOnRegistrationProps) => {
  const {data: checkIfOffersExistsResult} = useFetchCheckIfOffersExists({housingId});
  const [isMarkedAsShown, setIsMarkedAsShown] = useState(false);

  const storageKey = reservationId
    ? `${SHOWED_UPSELLING_DEALS_MODAL}-${reservationId}`
    : SHOWED_UPSELLING_DEALS_MODAL;

  const alreadyShownInSession = useMemo(() => {
    if (!storageKey || !reservationId) return false;

    const storedData = SessionStorage.get<{id?: string; showed?: boolean}>(storageKey);
    return storedData?.id == reservationId && storedData?.showed;
  }, [storageKey, reservationId]);

  const shouldShowDeals = useMemo(() => {
    if (alreadyShownInSession || isMarkedAsShown) return false;
    return Boolean(
      summary?.upselling?.active && checkIfOffersExistsResult?.result && enabled,
    );
  }, [
    alreadyShownInSession,
    isMarkedAsShown,
    summary?.upselling?.active,
    checkIfOffersExistsResult?.result,
    enabled,
  ]);

  const markDealsAsShown = useCallback(() => {
    setIsMarkedAsShown(true);
    if (!storageKey || !reservationId) return;
    SessionStorage.set(storageKey, {id: reservationId, showed: true});
  }, [storageKey, reservationId]);

  return {shouldShowDeals, markDealsAsShown};
};
